import { clamp } from "./utils.ts";
/**
 * The color math the theme-preset generator runs on.
 *
 * Nothing here paints. It exists so `scripts/generate-tokens.ts` can SOLVE a
 * token's lightness against a contrast target instead of a human guessing one,
 * and so the test suite can assert the result. Runtime code never calls it —
 * every color a component paints is a literal in `tokens.css`.
 *
 * Two facts drive every function below.
 *
 * The first is that a neutral's luminance goes as roughly the CUBE of its
 * OKLCH lightness. So adding a constant to L adds a wildly non-constant amount
 * of luminance ratio depending on where the token started, and ratios — not
 * differences — are what both the eye and every contrast model consume. A ramp
 * of surfaces is a ladder of luminance ratios; translating it by a constant
 * flattens the end with the least headroom and leaves the other end alone.
 *
 * The second is that CSS cannot express the fix. There is no contrast function
 * in `calc()`, so a token whose job is "stay legible on whatever is behind it"
 * cannot be written as an expression — it has to be solved, ahead of time, and
 * emitted as a literal.
 */

/** An OKLCH color. `l` is 0–100 to match the percentage CSS wants. */
export interface Oklch {
  l: number;
  c: number;
  h: number;
}


/** OKLCH to linear sRGB before gamut clipping. */
export function oklchToUnclampedLinearSrgb(
  color: Oklch,
): [number, number, number] {
  const l = color.l / 100;
  const hue = (color.h * Math.PI) / 180;
  const a = color.c * Math.cos(hue);
  const b = color.c * Math.sin(hue);

  const lCone = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const mCone = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const sCone = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return [
    4.0767416621 * lCone - 3.3077115913 * mCone + 0.2309699292 * sCone,
    -1.2684380046 * lCone + 2.6097574011 * mCone - 0.3413193965 * sCone,
    -0.0041960863 * lCone - 0.7034186147 * mCone + 1.707614701 * sCone,
  ];
}

/**
 * OKLCH to linear sRGB, channels clamped into gamut.
 *
 * Clamping rather than gamut-mapping is deliberate and only sound because of
 * how it is used: the caller measures contrast on the result, and a browser
 * painting an out-of-gamut color lands somewhere very close to the clamp. A
 * color picker would need the real mapping; a contrast probe does not.
 */
export function oklchToLinearSrgb(color: Oklch): [number, number, number] {
  return oklchToUnclampedLinearSrgb(color).map((value) => clamp(value, 0, 1)) as [
    number,
    number,
    number,
  ];
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance(color: Oklch): number {
  const [r, g, b] = oklchToLinearSrgb(color);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG 2.x contrast ratio, 1–21.
 *
 * This is the conformance floor and nothing more. Its `+ 0.05` flare term
 * dominates the denominator as luminance approaches zero, so the ratio stops
 * discriminating exactly where a dark theme lives: a surface ramp can collapse
 * from five distinct fills to one and still report over 8:1. Assert it because
 * a VPAT is written against it; tune against {@link apcaContrast}.
 */
export function wcagContrast(a: Oklch, b: Oklch): number {
  const first = relativeLuminance(a);
  const second = relativeLuminance(b);
  const hi = Math.max(first, second);
  const lo = Math.min(first, second);
  return (hi + 0.05) / (lo + 0.05);
}

// APCA-W3 0.0.98G-4g constants, sRGB.
const APCA_BLACK_THRESHOLD = 0.022;
const APCA_BLACK_CLAMP = 1.414;
const APCA_DELTA_Y_MIN = 0.0005;
const APCA_LO_CLIP = 0.1;
const APCA_SCALE = 1.14;
const APCA_OFFSET = 0.027;

// A screen-flare floor: below it, discrimination falls away faster than a
// luminance ratio predicts. This curve is what WCAG 2's flat `+ 0.05` was
// standing in for, and it is why APCA can tell a dark ramp apart at all.
const softClamp = (y: number): number =>
  y > APCA_BLACK_THRESHOLD
    ? y
    : y + (APCA_BLACK_THRESHOLD - y) ** APCA_BLACK_CLAMP;

/**
 * APCA lightness contrast, returned as a magnitude (0–~108).
 *
 * The two polarities take different exponents because they are not the same
 * perceptual task: light text on a dark field behaves closer to a point source
 * than to a large patch, and follows a different power curve. That asymmetry
 * is the whole reason a dark theme cannot be tuned with a symmetric ratio.
 *
 * Thresholds this system holds tokens to: body text 75 (90 preferred),
 * secondary text 60, any text at all 30, and non-text that must merely be
 * discernible 15.
 */
export function apcaContrast(text: Oklch, background: Oklch): number {
  const textY = softClamp(relativeLuminance(text));
  const bgY = softClamp(relativeLuminance(background));
  if (Math.abs(bgY - textY) < APCA_DELTA_Y_MIN) return 0;

  if (bgY > textY) {
    const contrast = (bgY ** 0.56 - textY ** 0.57) * APCA_SCALE;
    return contrast < APCA_LO_CLIP ? 0 : (contrast - APCA_OFFSET) * 100;
  }
  const contrast = (bgY ** 0.65 - textY ** 0.62) * APCA_SCALE;
  return contrast > -APCA_LO_CLIP ? 0 : -(contrast + APCA_OFFSET) * 100;
}

// Ottosson's toe constants.
const TOE_K1 = 0.206;
const TOE_K2 = 0.03;
const TOE_K3 = (1 + TOE_K1) / (1 + TOE_K2);

/**
 * OKLCH lightness to REFERENCE lightness, both 0–100.
 *
 * OKLCH's own L is scale-independent, which costs it accuracy as a lightness
 * predictor once there is a defined white — a screen. Ottosson published this
 * toe for that case; it tracks CIELAB L\* closely, and it is the axis on which
 * an equal step is actually an equal step. Every ramp transform here runs in
 * this space, because the same pivot applied to raw L blows the darkest rung's
 * luminance ratio out by a factor of three.
 */
export function toReferenceLightness(l: number): number {
  const x = TOE_K3 * (l / 100) - TOE_K1;
  return ((x + Math.sqrt(x * x + 4 * TOE_K2 * TOE_K3 * (l / 100))) / 2) * 100;
}

/** Inverse of {@link toReferenceLightness}. */
export function fromReferenceLightness(lr: number): number {
  const r = lr / 100;
  return ((r * (r + TOE_K1)) / (TOE_K3 * (r + TOE_K2))) * 100;
}

/** The lightness a ramp may not pass, per mode. Both ends, in OKLCH L. */
export interface RampBounds {
  floor: number;
  ceiling: number;
}

/**
 * Re-seats a surface ramp so its anchor rung lands on `anchorTarget`.
 *
 * Affine on the reference axis: every rung keeps its share of the ramp's span,
 * and the span itself scales only as far as the remaining headroom allows. Two
 * properties fall out, and both are the point.
 *
 * It is strictly order-preserving. An affine map cannot reorder its input, so
 * no preset can make a well read lighter than the page it is cut into — which
 * is the failure an additive shift produces the moment one rung reaches the 0
 * or 100 wall and the others keep going.
 *
 * And it never reaches the wall at all. The scale is chosen so the extreme
 * rungs land inside `bounds`, so nothing clamps, nothing merges, and the
 * ramp's internal ratios survive intact. The cost is honest and visible: at
 * the ends the ramp is tighter, which is why the hairlines are solved rather
 * than fixed — as the fills converge the hairlines darken to carry the
 * separation the fills gave up.
 */
export function reseatRamp(
  values: readonly number[],
  anchorIndex: number,
  anchorTarget: number,
  bounds: RampBounds,
): number[] {
  const reference = values.map(toReferenceLightness);
  const anchor = reference[anchorIndex]!;
  const target = toReferenceLightness(anchorTarget);
  const floor = toReferenceLightness(bounds.floor);
  const ceiling = toReferenceLightness(bounds.ceiling);

  const above = Math.max(...reference) - anchor;
  const below = anchor - Math.min(...reference);
  const scale = Math.min(
    1,
    above > 0 ? (ceiling - target) / above : 1,
    below > 0 ? (target - floor) / below : 1,
  );

  return reference.map((value) =>
    fromReferenceLightness(target + (value - anchor) * scale),
  );
}

/**
 * The lightness at which `color` hits `target` APCA against `background`.
 *
 * Bisection, on lightness alone — hue and chroma are passed through untouched.
 * That is the property that keeps a solved token recognisable: the red stays
 * the same red, it only stops sitting at a lightness where it cannot be read.
 * Chroma is carried proportionally by {@link holdApparentSaturation}, which is
 * a separate decision the caller makes.
 *
 * `direction` says which side of the background to search. Returns the closest
 * reachable lightness when the target is not achievable at all, so a caller
 * that needs to know asks {@link apcaContrast} about the answer rather than
 * getting a null it has to thread everywhere.
 */
export function solveLightnessForApca(
  target: number,
  background: Oklch,
  color: Omit<Oklch, "l">,
  direction: 1 | -1,
): number {
  let lo = direction > 0 ? background.l : 0;
  let hi = direction > 0 ? 100 : background.l;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    const reached = apcaContrast({ ...color, l: mid }, background);
    if (reached < target) {
      if (direction > 0) lo = mid;
      else hi = mid;
    } else if (direction > 0) hi = mid;
    else lo = mid;
  }
  return (lo + hi) / 2;
}

/**
 * Chroma rescaled so the color's APPARENT saturation survives a move in
 * lightness.
 *
 * The eye reads saturation as chroma relative to lightness, so a fixed chroma
 * carried from L 14 to L 34 loses well over half its apparent colorfulness —
 * and the Hunt effect widens the gap, because a brighter sample is expected to
 * be MORE colorful, not less. For the achromatic spine this is a no-op; it is
 * the faintly tinted hairlines it keeps from flattening into dead grey.
 */
export function holdApparentSaturation(
  chroma: number,
  fromL: number,
  toL: number,
): number {
  if (chroma === 0 || fromL === 0) return chroma;
  return chroma * (toL / fromL);
}

/**
 * A background lightness a foreground can actually be placed against.
 *
 * Between 50 and 60 neither black nor white text clears 4.5:1, so a surface
 * that lands there has no legible foreground in either direction — the literal
 * muddy band. Backgrounds evacuate it; foregrounds are free to sit anywhere.
 */
export function avoidDeadBand(l: number): number {
  if (l < 50 || l >= 60) return l;
  return l < 55 ? 49 : 60;
}

/** Serializes to the `oklch()` form `tokens.css` is written in. */
export function formatOklch({ l, c, h }: Oklch, alpha?: number): string {
  const round = (value: number, places: number): string =>
    Number(value.toFixed(places)).toString();
  const body = `${round(Math.min(100, Math.max(0, l)), 2)}% ${round(c, 4)} ${round(h, 2)}`;
  return alpha === undefined ? `oklch(${body})` : `oklch(${body} / ${alpha})`;
}
